import { createTheme, alpha } from '@mui/material/styles';

// Sign-in palette (option A+): cream, terracotta, espresso header and deep teal
export const vibrantColors = {
  light: {
    bg: '#f6f1e6', paper: '#fffcf6', ink: '#2c2418', muted: '#625a4d', line: 'rgba(44, 36, 24, 0.2)',
    primary: '#b5541f', primaryLight: '#d9773f', primaryDark: '#9c4517', onPrimary: '#ffffff',
    secondary: '#3c5c52', secondaryLight: '#5f8072', secondaryDark: '#2f4a42', onSecondary: '#ffffff',
    link: '#9c4517', header: '#241d13', headerInk: '#f6f1e6',
    highlight: 'rgba(181, 84, 31, 0.16)', highlightActive: 'rgba(181, 84, 31, 0.44)',
    error: '#9e2a3a', warning: '#875609', info: '#3d6475', success: '#3d6a32',
    shadow: 'rgba(44, 36, 24, 0.10)', shadowHover: 'rgba(44, 36, 24, 0.16)',
  },
  dark: {
    bg: '#1d1813', paper: '#29221a', ink: '#f0e6d4', muted: '#b3a690', line: 'rgba(240, 230, 212, 0.16)',
    primary: '#d9773f', primaryLight: '#e08a55', primaryDark: '#b5541f', onPrimary: '#1d1813',
    secondary: '#7fa596', secondaryLight: '#a9cbbd', secondaryDark: '#5f8072', onSecondary: '#1d1813',
    link: '#eda274', header: '#120e09', headerInk: '#f0e6d4',
    highlight: 'rgba(217, 119, 63, 0.24)', highlightActive: 'rgba(217, 119, 63, 0.5)',
    error: '#f08a96', warning: '#e0a94a', info: '#86aec0', success: '#8fbf7d',
    shadow: 'rgba(0, 0, 0, 0.3)', shadowHover: 'rgba(0, 0, 0, 0.4)',
  },
};

export const createVibrantTheme = (mode = 'light') => {
  const c = vibrantColors[mode === 'dark' ? 'dark' : 'light'];
  const primaryColor = c.primary;
  const secondaryColor = c.secondary;
  
  // Common colors used across components (including PageHeader)
  const commonColors = {
    // Colors for access level chips
    accessLevels: {
      READ: {
        text: mode === 'light' ? '#1565c0' : 'rgba(179, 229, 252, 0.9)',
        border: 'rgba(66, 165, 245, 1)',
        bg: mode === 'light' ? 'rgba(66, 165, 245, 0.1)' : 'rgba(25, 118, 210, 0.2)'
      },
      WRITE: {
        text: mode === 'light' ? '#2e7d32' : 'rgba(185, 246, 202, 0.9)',
        border: 'rgba(102, 187, 106, 1)',
        bg: mode === 'light' ? 'rgba(102, 187, 106, 0.1)' : 'rgba(76, 175, 80, 0.2)'
      },
      ADMIN: {
        text: mode === 'light' ? '#c62828' : 'rgba(255, 205, 210, 0.9)',
        border: 'rgba(239, 83, 80, 1)',
        bg: mode === 'light' ? 'rgba(239, 83, 80, 0.1)' : 'rgba(198, 40, 40, 0.2)'
      }
    },
    // Colors for search highlighting
    search: {
      highlight: c.highlight,
      active: c.highlightActive,
      text: c.link,
      border: c.primary
    },
    // Colors for folder/file icons
    icons: {
      folder: c.primary,
      video: c.secondary,
      action: c.link,
      default: c.muted
    },
    // Login page specific colors
    login: {
      background: c.bg,
      cardBg: c.paper,
      cardBorder: c.line,
      inputBorder: c.line,
      buttonGradient: c.primary
    },
    // Admin page specific colors
    admin: {
      background: c.bg,
      cardBg: c.paper,
      navbarBg: c.header,
      tableBgHover: alpha(c.primary, mode === 'light' ? 0.08 : 0.16),
      dashboardCardBg: c.paper
    }
  };
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: c.primaryLight,
        dark: c.primaryDark,
        contrastText: c.onPrimary,
      },
      secondary: {
        main: secondaryColor,
        light: c.secondaryLight,
        dark: c.secondaryDark,
        contrastText: c.onSecondary,
      },
      warning: { main: c.warning },
      success: { main: c.success },
      error: { main: c.error },
      info: { main: c.info },
      background: {
        default: c.bg, // Cream / warm brown-black
        paper: c.paper,
      },
      text: {
        primary: c.ink,
        secondary: c.muted,
      },
      divider: c.line,
      // Solid header color from the sign-in photo panel
      header: {
        bg: c.header,
        text: c.headerInk,
      },
      // Add searchHighlight to the theme palette for easy access
      searchHighlight: {
        bg: commonColors.search.highlight,
        activeBg: commonColors.search.active,
        text: commonColors.search.text,
        border: commonColors.search.border
      },
      // Add accessLevels to theme palette
      accessLevels: commonColors.accessLevels,
      // Add icon colors to theme palette
      iconColors: commonColors.icons,
      // Add login colors
      login: commonColors.login,
      // Add admin colors
      admin: commonColors.admin
    },
    typography: {
      // Sign-in page fonts: Marcellus for headings, Karla for everything else
      fontFamily: '"Karla", "Helvetica Neue", "Arial", sans-serif',
      // Make headings more compact
      h1: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.01em',
        marginBottom: '0.5rem' // Reduced margin
      },
      h2: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.01em',
        marginBottom: '0.5rem' // Reduced margin
      },
      h3: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        letterSpacing: '-0.01em',
        marginBottom: '0.4rem' // Reduced margin
      },
      h4: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        marginBottom: '0.3rem' // Reduced margin
      },
      h5: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        marginBottom: '0.2rem' // Reduced margin
      },
      h6: { 
        fontFamily: '"Marcellus", Georgia, serif',
        fontWeight: 400,
        marginBottom: '0.2rem' // Reduced margin
      },
      // Make body text more compact
      body1: {
        fontFamily: '"Karla", sans-serif',
        fontWeight: 400,
        lineHeight: 1.5, // Slightly reduced from 1.65
      },
      body2: {
        fontFamily: '"Karla", sans-serif',
        fontWeight: 400,
        lineHeight: 1.5, // Slightly reduced from 1.65
      },
      button: {
        fontFamily: '"Karla", sans-serif',
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '0.02em',
      },
      caption: {
        fontFamily: '"Karla", sans-serif',
        fontWeight: 400,
      },
      overline: {
        fontFamily: '"Karla", sans-serif',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
      },
    },
    shape: {
      borderRadius: 12,
    },
    // Custom spacing system - overriding MUI's default spacing
    spacing: factor => `${0.6 * factor}rem`, // Reduced from 0.75 to 0.6 for more compact spacing
    components: {
      // Make general spacing more compact in all components
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '12px', // Reduced from 16px
            paddingRight: '12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              paddingLeft: '16px', // Reduced from 24px
              paddingRight: '16px', // Reduced from 24px
            },
          }
        }
      },
      // Reduce card padding
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12, // Reduced from 16
            boxShadow: mode === 'light'
              ? `0 6px 16px ${c.shadow}` // Reduced shadow
              : `0 6px 16px ${c.shadow}`, // Reduced shadow
            overflow: 'hidden',
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'translateY(-3px)', // Reduced from -5px
              boxShadow: mode === 'light'
                ? `0 10px 25px ${c.shadowHover}` // Reduced shadow
                : '0 10px 25px rgba(0, 0, 0, 0.25)', // Reduced shadow
            },
          }
        }
      },
      // Make card content more compact
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '12px', // Reduced from 16px
            '&:last-child': {
              paddingBottom: '12px', // Reduced from 16px
            },
            [theme => theme.breakpoints.up('sm')]: {
              padding: '16px', // Reduced from 20px
              '&:last-child': {
                paddingBottom: '16px', // Reduced from 20px
              },
            },
          }
        }
      },
      // Make paper components more compact
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: '12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              padding: '16px', // Reduced from 20px
            },
            '&.admin-dashboard-card': {
              background: commonColors.admin.dashboardCardBg,
              borderRadius: 12, // Reduced from 16
              boxShadow: mode === 'light'
                ? `0 6px 16px ${c.shadow}` // Reduced shadow
                : `0 6px 16px ${c.shadow}`, // Reduced shadow
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-3px)', // Reduced from -5px
                boxShadow: mode === 'light'
                  ? `0 10px 25px ${c.shadowHover}` // Reduced shadow
                  : '0 10px 25px rgba(0, 0, 0, 0.25)', // Reduced shadow
              }
            }
          }
        }
      },
      // Update AppBar to be more compact
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: c.header,
            backgroundImage: 'none',
            color: c.headerInk,
            boxShadow: '0 3px 8px rgba(0, 0, 0, 0.12)', // Reduced shadow
            borderRadius: '8px', // Reduced from 12px
            margin: '0 8px 8px 8px', // Reduced from 12px
            width: 'auto'
          },
        },
      },
      // Make toolbar more compact
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: '48px', // Reduced from 56px
            [theme => theme.breakpoints.up('sm')]: {
              minHeight: '56px', // Reduced from 60px
            },
            padding: '0 12px', // Reduced from 16px
            [theme => theme.breakpoints.up('sm')]: {
              padding: '0 16px', // Reduced from 20px
            },
          },
          // Ensure that dense toolbars are even more compact
          dense: {
            minHeight: '40px', // Reduced from 48px
            [theme => theme.breakpoints.up('sm')]: {
              minHeight: '44px', // Reduced from 52px
            },
          },
        },
      },
      // Make buttons more compact
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6, // Reduced from 8
            padding: '6px 12px', // Reduced from 8px 16px
            fontWeight: 600,
            transition: 'all 0.25s', // Reduced from 0.3s
            '&:hover': {
              transform: 'translateY(-1px)', // Reduced from -2px
              boxShadow: mode === 'light' 
                ? `0 4px 12px ${c.shadowHover}` // Reduced shadow 
                : `0 4px 12px ${c.shadowHover}`, // Reduced shadow
            },
            // Make small buttons even more compact
            '&.MuiButton-sizeSmall': {
              padding: '3px 8px', // Reduced from 4px 10px
              fontSize: '0.75rem', // Reduced from 0.8125rem
            },
          },
        },
        variants: [
          {
            props: { variant: 'vsCodeBlue' },
            style: {
              backgroundColor: secondaryColor,
              color: c.onSecondary,
              height: '35px',
              border: 'none', 
              borderRadius: '4px',
              padding: '6px 12px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'background 0.2s ease',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: c.secondaryDark,
                transform: 'none',
                boxShadow: 'none',
              },
              '&:disabled': {
                opacity: 0.5,
                pointerEvents: 'none',
              }
            }
          }
        ]
      },
      // Make form fields more compact
      MuiTextField: {
        defaultProps: {
          margin: 'dense',
          size: 'small',
        },
        styleOverrides: {
          root: {
            marginTop: '6px', // Reduced from 8px
            marginBottom: '6px', // Reduced from 8px
            '& .MuiInputBase-root': {
              padding: '4px 8px', // More compact padding
            },
            // Keep search highlight styling
          }
        }
      },
      // Keep using small selects
      MuiSelect: {
        defaultProps: {
          size: 'small',
        }
      },
      // Make form control more compact
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginTop: '6px', // Reduced from 8px
            marginBottom: '6px', // Reduced from 8px
          }
        }
      },
      // Make dialogs more compact
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: '8px', // Reduced from 12px
          }
        }
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '8px 12px', // Reduced from 12px 16px
          }
        }
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced from 8px 16px
            '&:first-of-type': {
              paddingTop: '8px', // Reduced from 12px
            }
          }
        }
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced from 8px 16px
          }
        }
      },
      // Make lists more compact
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: '2px', // Reduced from 4px
            paddingBottom: '2px', // Reduced from 4px
          }
        }
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: '4px', // Reduced from 6px
            paddingBottom: '4px', // Reduced from 6px
          }
        }
      },
      // Reduce menu item padding
      MuiMenuItem: {
        styleOverrides: {
          root: {
            minHeight: '32px', // Reduced from 40px
            paddingTop: '4px', // Reduced from 6px
            paddingBottom: '4px', // Reduced from 6px
          }
        }
      },
      // Styling for login page (more compact)
      MuiCssBaseline: {
        styleOverrides: {
          // Reduce login card padding
          '.login-card': {
            background: commonColors.login.cardBg,
            backdropFilter: 'blur(8px)',
            borderRadius: 12, // Reduced from 16
            border: `1px solid ${commonColors.login.cardBorder}`,
            boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)', // Reduced from 32px
            padding: '16px', // Reduced from 24px
            position: 'relative',
            zIndex: 1
          },
          // Add global styles to reduce spacing in all components
          'body': {
            '& .MuiBox-root': {
              margin: '0',
            },
            '& .MuiTypography-root': {
              marginBottom: '0.25em', // Reduced from 0.35em
            },
            '& .MuiCardHeader-root': {
              padding: '8px 12px', // Reduced from 12px 16px
            },
            // Add more global spacing reductions
            '& .MuiAccordionSummary-content': {
              margin: '8px 0', // Reduced from 12px
            },
            '& .MuiCardActions-root': {
              padding: '6px', // Reduced padding
            }
          }
        }
      },
      // Reduce spacing in table rows
      MuiTableRow: {
        styleOverrides: {
          root: {
            '& > .MuiTableCell-root': {
              padding: '6px 12px', // Reduced from 8px 16px
            },
            '&:hover': {
              backgroundColor: commonColors.admin.tableBgHover
            }
          }
        }
      },
      // Add TableCell styling for more compact tables
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '6px 12px', // Reduced padding
          },
          head: {
            fontWeight: 600,
            fontSize: '0.8rem', // Smaller font for headers
          },
          body: {
            fontSize: '0.8rem', // Smaller font for body
          }
        }
      },
      // Make tabs more compact
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
            padding: '4px 12px', // Reduced from 6px 16px
            fontSize: '0.8rem', // Smaller font size
          }
        }
      },
      // Make accordion more compact
      MuiAccordion: {
        styleOverrides: {
          root: {
            margin: '6px 0', // Reduced from 8px
          }
        }
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            minHeight: '40px', // Reduced from 48px
            padding: '0 12px', // Reduced from 0 16px
          },
          content: {
            margin: '8px 0', // Reduced from 12px
          }
        }
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '6px 12px 12px', // Reduced from 8px 16px 16px
          }
        }
      },
      // Reduce Typography margins
      MuiTypography: {
        styleOverrides: {
          root: {
            marginBottom: '0.25em', // Reduced margin
          },
          paragraph: {
            marginBottom: '0.5em', // Reduced from default
          }
        }
      },
      // Make Chips more compact
      MuiChip: {
        styleOverrides: {
          root: {
            height: '24px', // Reduced height
            fontSize: '0.75rem', // Smaller font
          },
          sizeSmall: {
            height: '20px', // Even smaller
          },
          label: {
            padding: '0 8px', // Reduced padding
          },
          filledPrimary: {
            background: primaryColor,
          },
          filledSecondary: {
            background: secondaryColor,
          }
        },
        variants: [
          {
            props: { variant: 'accessLevel', color: 'read' },
            style: {
              backgroundColor: commonColors.accessLevels.READ.bg,
              color: commonColors.accessLevels.READ.text,
              border: `1px solid ${commonColors.accessLevels.READ.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          },
          {
            props: { variant: 'accessLevel', color: 'write' },
            style: {
              backgroundColor: commonColors.accessLevels.WRITE.bg,
              color: commonColors.accessLevels.WRITE.text,
              border: `1px solid ${commonColors.accessLevels.WRITE.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          },
          {
            props: { variant: 'accessLevel', color: 'admin' },
            style: {
              backgroundColor: commonColors.accessLevels.ADMIN.bg,
              color: commonColors.accessLevels.ADMIN.text,
              border: `1px solid ${commonColors.accessLevels.ADMIN.border}`,
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 1, fontSize: '0.7rem', fontWeight: 'bold' }
            }
          }
        ]
      
    },
    // Custom variables for the theme
    customVars: {
      searchHighlight: {
        backgroundColor: commonColors.search.highlight,
        activeBgColor: commonColors.search.active,
        textColor: commonColors.search.text,
        borderColor: commonColors.search.border,
        borderRadius: '2px',
        padding: '0 2px',
        margin: '0 -2px',
      },
      accessLevels: commonColors.accessLevels,
      icons: commonColors.icons,
      login: commonColors.login,
      admin: commonColors.admin
    },
    }
  })
};

// Icon colors from the sign-in palette
export const getVibrantIconColor = (type, darkMode) => {
  switch (type) {
    case 'folder':
      return darkMode ? vibrantColors.dark.primary : vibrantColors.light.primary; // Terracotta for folders
    case 'video':
      return darkMode ? vibrantColors.dark.secondary : vibrantColors.light.secondary; // Teal for videos
    case 'action':
      return darkMode ? vibrantColors.dark.link : vibrantColors.light.link; // Action buttons
    default:
      return darkMode ? vibrantColors.dark.muted : vibrantColors.light.muted; // Grey default
  }
};

// Keep existing helper functions
export const getVibrantChipTextColor = (access, darkMode) => {
  switch (access) {
    case 'READ':
      return darkMode ? 'rgba(179, 229, 252, 0.9)' : '#1565c0'; // Blue
    case 'WRITE': 
      return darkMode ? 'rgba(185, 246, 202, 0.9)' : '#2e7d32'; // Green
    case 'ADMIN':
      return darkMode ? 'rgba(255, 205, 210, 0.9)' : '#c62828'; // Red
    default:
      return darkMode ? 'rgba(224, 224, 224, 0.9)' : '#424242'; // Gray
  }
};

export const getVibrantChipBorderColor = (access) => {
  switch (access) {
    case 'READ':
      return 'rgba(66, 165, 245, 1)'; // Blue border
    case 'WRITE': 
      return 'rgba(102, 187, 106, 1)'; // Green border
    case 'ADMIN':
      return 'rgba(239, 83, 80, 1)'; // Red border
    default:
      return 'rgba(189, 189, 189, 1)'; // Gray border
  }
};

export const getVibrantChipBgColor = (access, darkMode) => {
  switch (access) {
    case 'READ':
      return darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(66, 165, 245, 0.1)'; 
    case 'WRITE': 
      return darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(102, 187, 106, 0.1)'; 
    case 'ADMIN':
      return darkMode ? 'rgba(198, 40, 40, 0.2)' : 'rgba(239, 83, 80, 0.1)'; 
    default:
      return darkMode ? 'rgba(117, 117, 117, 0.2)' : 'rgba(189, 189, 189, 0.1)'; 
  }
};